/**
 * Brand logo resolver — rewrites <img> tags in LLM-generated email bodies
 * so brand logos actually render.
 *
 * LLMs often emit placeholder URLs like "microsoft365-logo.png" or
 * unreachable paths. We swap those for inline SVG data URIs keyed by
 * brand — guaranteed to render offline, no network, no CORS.
 */

type Logo = { name: string; keywords: string[]; svg: string }

const encode = (svg: string) =>
  'data:image/svg+xml;utf8,' + encodeURIComponent(svg.replace(/\s+/g, ' ').trim())

const MICROSOFT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="96" height="96">
  <path fill="#f25022" d="M1 1h10v10H1z"/>
  <path fill="#7fba00" d="M12 1h10v10H12z"/>
  <path fill="#00a4ef" d="M1 12h10v10H1z"/>
  <path fill="#ffb900" d="M12 12h10v10H12z"/>
</svg>`

const GOOGLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="96" height="96">
  <path fill="#4285f4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
  <path fill="#34a853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
  <path fill="#fbbc04" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
  <path fill="#ea4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/>
</svg>`

const DOCUSIGN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="96" height="96">
  <rect width="100" height="100" rx="12" fill="#ffcc22"/>
  <text x="50" y="62" text-anchor="middle" font-family="Arial,sans-serif" font-weight="bold" font-size="36" fill="#000">D</text>
</svg>`

const PAYPAL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 32" width="120" height="38">
  <text x="0" y="24" font-family="Arial,sans-serif" font-weight="bold" font-size="26" fill="#003087">Pay</text>
  <text x="46" y="24" font-family="Arial,sans-serif" font-weight="bold" font-size="26" fill="#009cde">Pal</text>
</svg>`

const DROPBOX_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 40" width="96" height="92">
  <path fill="#0061ff" d="M10.5 0 0 6.5 10.5 13 21 6.5zM31.5 0 21 6.5 31.5 13 42 6.5zM0 19.5 10.5 26 21 19.5 10.5 13zM31.5 13 21 19.5 31.5 26 42 19.5zM10.5 28.5 21 35 31.5 28.5 21 22z"/>
</svg>`

const ADOBE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 96" width="96" height="92">
  <rect width="100" height="96" rx="12" fill="#eb1000"/>
  <text x="50" y="66" text-anchor="middle" font-family="Arial,sans-serif" font-weight="bold" font-size="52" fill="#fff">A</text>
</svg>`

const LINKEDIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="96" height="96">
  <rect width="32" height="32" rx="4" fill="#0a66c2"/>
  <text x="16" y="22" text-anchor="middle" font-family="Arial,sans-serif" font-weight="bold" font-size="18" fill="#fff">in</text>
</svg>`

const AMAZON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 40" width="140" height="46">
  <text x="0" y="26" font-family="Arial,sans-serif" font-weight="bold" font-size="26" fill="#232f3e">amazon</text>
  <path d="M8 32 q52 12 100 0" stroke="#ff9900" stroke-width="3" fill="none" stroke-linecap="round"/>
</svg>`

const SLACK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="96" height="96">
  <path fill="#e01e5a" d="M8 20a2 2 0 1 1 0-4h2v4zM9 20h2a2 2 0 0 1 0 4H9a2 2 0 0 1-2-2z"/>
  <path fill="#36c5f0" d="M12 8a2 2 0 1 1 4 0v2h-4zM12 9v2a2 2 0 0 1-4 0V9a2 2 0 0 1 2-2z"/>
  <path fill="#2eb67d" d="M24 12a2 2 0 1 1 0 4h-2v-4zM23 12h-2a2 2 0 0 1 0-4h2a2 2 0 0 1 2 2z"/>
  <path fill="#ecb22e" d="M20 24a2 2 0 1 1-4 0v-2h4zM20 23v-2a2 2 0 0 1 4 0v2a2 2 0 0 1-2 2z"/>
</svg>`

const ZOOM_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 40" width="120" height="48">
  <rect width="100" height="40" rx="6" fill="#2d8cff"/>
  <text x="50" y="26" text-anchor="middle" font-family="Arial,sans-serif" font-weight="bold" font-size="18" fill="#fff">zoom</text>
</svg>`

const APPLE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="96" height="96">
  <path fill="#000" d="M22 16.6c0-3.3 2.7-4.9 2.8-5-1.5-2.2-3.9-2.5-4.7-2.5-2-.2-3.9 1.2-4.9 1.2s-2.6-1.2-4.3-1.1c-2.2 0-4.2 1.3-5.4 3.3-2.3 4-.6 9.8 1.7 13 1.1 1.6 2.4 3.3 4.1 3.3 1.6-.1 2.3-1.1 4.3-1.1s2.5 1.1 4.3 1c1.8 0 2.9-1.6 4-3.2 1.3-1.8 1.8-3.6 1.8-3.7 0 0-3.5-1.3-3.5-5.2zM18.9 7.1c.9-1.1 1.5-2.5 1.3-4-1.3.1-2.8.9-3.7 2-.8.9-1.5 2.4-1.3 3.8 1.4.1 2.9-.7 3.7-1.8z"/>
</svg>`

const OKTA_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 32" width="90" height="30">
  <circle cx="15" cy="19" r="9" fill="none" stroke="#00297a" stroke-width="5"/>
  <text x="29" y="26" font-family="Arial,sans-serif" font-weight="bold" font-size="22" fill="#00297a">kta</text>
</svg>`

// Neutral safety-net mark for an unmatched / unknown logo — a muted emblem plus
// wordmark bars. Deliberately renders NO literal "LOGO" text (that gray "LOGO"
// box is the exact eyesore this replaces); it just reads as "a logo goes here".
const GENERIC_LOGO = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 40" width="120" height="40">
  <rect width="120" height="40" rx="6" fill="#eceef1"/>
  <circle cx="22" cy="20" r="9" fill="none" stroke="#b6bcc6" stroke-width="2.5"/>
  <rect x="40" y="14" width="56" height="5" rx="2.5" fill="#c4c9d2"/>
  <rect x="40" y="23" width="36" height="4" rx="2" fill="#d6dae0"/>
</svg>`

// Neutral stylized QR-code placeholder. The SafeAttack seeds carry realistic
// decoy QR URLs (…/qr/*.png) that never resolve — that is intentional pretext.
// Rather than show a broken-image icon in a preview, we swap those for this
// muted, plausible QR square (the seed URL itself is never modified).
const QR_PLACEHOLDER = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="150" height="150">
  <rect width="100" height="100" rx="6" fill="#ffffff" stroke="#d1d5db" stroke-width="1"/>
  <g fill="#4b5563">
    <rect x="8" y="8" width="22" height="22" rx="2"/>
    <rect x="70" y="8" width="22" height="22" rx="2"/>
    <rect x="8" y="70" width="22" height="22" rx="2"/>
  </g>
  <g fill="#ffffff">
    <rect x="13" y="13" width="12" height="12" rx="1"/>
    <rect x="75" y="13" width="12" height="12" rx="1"/>
    <rect x="13" y="75" width="12" height="12" rx="1"/>
  </g>
  <g fill="#4b5563">
    <rect x="16" y="16" width="6" height="6"/>
    <rect x="78" y="16" width="6" height="6"/>
    <rect x="16" y="78" width="6" height="6"/>
  </g>
  <g fill="#6b7280">
    <rect x="40" y="10" width="6" height="6"/><rect x="52" y="10" width="6" height="6"/>
    <rect x="46" y="22" width="6" height="6"/><rect x="58" y="28" width="6" height="6"/>
    <rect x="40" y="40" width="6" height="6"/><rect x="52" y="46" width="6" height="6"/>
    <rect x="64" y="40" width="6" height="6"/><rect x="76" y="46" width="6" height="6"/>
    <rect x="10" y="40" width="6" height="6"/><rect x="22" y="52" width="6" height="6"/>
    <rect x="40" y="64" width="6" height="6"/><rect x="52" y="70" width="6" height="6"/>
    <rect x="64" y="64" width="6" height="6"/><rect x="76" y="70" width="6" height="6"/>
    <rect x="40" y="82" width="6" height="6"/><rect x="64" y="82" width="6" height="6"/>
    <rect x="82" y="82" width="6" height="6"/>
  </g>
</svg>`

const LOGOS: Logo[] = [
  {
    name: 'Microsoft 365',
    keywords: ['microsoft 365', 'microsoft365', 'm365', 'office 365', 'office365', 'outlook', 'onedrive', 'sharepoint', 'teams', 'microsoft'],
    svg: MICROSOFT_SVG,
  },
  { name: 'Google',   keywords: ['google', 'gmail', 'gsuite', 'google workspace', 'workspace'], svg: GOOGLE_SVG },
  { name: 'DocuSign', keywords: ['docusign'],                                                    svg: DOCUSIGN_SVG },
  { name: 'Okta',     keywords: ['okta'],                                                        svg: OKTA_SVG },
  { name: 'PayPal',   keywords: ['paypal'],                                                      svg: PAYPAL_SVG },
  { name: 'Dropbox',  keywords: ['dropbox'],                                                     svg: DROPBOX_SVG },
  { name: 'Adobe',    keywords: ['adobe', 'acrobat'],                                            svg: ADOBE_SVG },
  { name: 'LinkedIn', keywords: ['linkedin'],                                                    svg: LINKEDIN_SVG },
  { name: 'Amazon',   keywords: ['amazon', 'aws'],                                               svg: AMAZON_SVG },
  { name: 'Slack',    keywords: ['slack'],                                                       svg: SLACK_SVG },
  { name: 'Zoom',     keywords: ['zoom'],                                                        svg: ZOOM_SVG },
  { name: 'Apple',    keywords: ['apple', 'icloud'],                                             svg: APPLE_SVG },
]

function findLogo(hint: string): Logo | null {
  const h = hint.toLowerCase()
  for (const logo of LOGOS) {
    if (logo.keywords.some((k) => h.includes(k))) return logo
  }
  return null
}

function attr(tag: string, name: string): string {
  const m = tag.match(new RegExp(`${name}\\s*=\\s*"([^"]*)"`, 'i')) ||
            tag.match(new RegExp(`${name}\\s*=\\s*'([^']*)'`, 'i'))
  return m ? m[1] : ''
}

function srcLooksBroken(src: string): boolean {
  if (!src) return true
  if (src.startsWith('data:')) return false
  if (src.startsWith('cid:')) return true
  // Absolute URL to a real host — leave alone (real CDN).
  if (/^https?:\/\//i.test(src)) {
    // Common LLM placeholder patterns
    return /placeholder|example\.com|your-?(logo|brand)|logo\.png$|logo\.jpg$|via\.placeholder/i.test(src)
  }
  // Relative paths and bare filenames — almost always broken in this context.
  return true
}

/** A QR image — either the alt says so, or the src path is a `/qr/` decoy. */
function isQrImage(src: string, alt: string): boolean {
  return /\bqr\b/i.test(alt) || /\/qr\//i.test(src)
}

function logoImg(svg: string, alt: string): string {
  return `<img src="${encode(svg)}" alt="${alt}" style="max-height:48px;max-width:180px;display:inline-block;vertical-align:middle;" />`
}

/**
 * Replaces broken / placeholder / deferred <img> tags so a logo always renders:
 *   1. QR decoys (alt "QR code" or a `/qr/` src) → a neutral inline QR square,
 *   2. `data-brand-key="slug"` → the FE inline SVG if we know that brand,
 *      else the backend PNG (`<base>/v1/brand-logos/by-slug/<slug>/png`) when a
 *      base URL is supplied, else the neutral generic mark,
 *   3. a broken/placeholder `src` → inline brand logo (by alt/src/brandHint) or
 *      the neutral generic mark.
 * A real, resolvable `src` (data:/known CDN) is left untouched.
 *
 * `backendBaseUrl` should be the SAME backend the app talks to (NEXT_PUBLIC_API_URL)
 * so a brand-key that isn't inline still loads from our own origin — see
 * lib/phishing/ai-studio/resolve-brand-logos.ts for the canonical base source.
 */
export function resolveBrandImages(
  html: string,
  brandHint?: string,
  backendBaseUrl?: string,
): string {
  if (!html) return html
  const base = (backendBaseUrl ?? '').replace(/\/$/, '')
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = attr(tag, 'src')
    const alt = attr(tag, 'alt')
    const brandKey = attr(tag, 'data-brand-key')

    // 1) QR decoy → neutral inline QR square (seed URL never resolves).
    if (isQrImage(src, alt)) {
      return `<img src="${encode(QR_PLACEHOLDER)}" alt="${alt || 'QR code'}" style="max-width:160px;height:auto;display:inline-block;" />`
    }

    // 2) Deferred brand-key placeholder → inline SVG, else backend PNG.
    if (brandKey) {
      const logo = findLogo(`${brandKey} ${alt}`)
      if (logo) return logoImg(logo.svg, alt || `${logo.name} Logo`)
      if (base) {
        return `<img src="${base}/v1/brand-logos/by-slug/${encodeURIComponent(brandKey)}/png" alt="${alt || 'Logo'}" style="max-height:48px;max-width:180px;display:inline-block;vertical-align:middle;" />`
      }
      return logoImg(GENERIC_LOGO, alt || 'Logo')
    }

    // 3) Broken/placeholder src → inline brand logo or neutral generic mark.
    if (!srcLooksBroken(src)) return tag
    const hint = `${alt} ${src} ${brandHint || ''}`
    const logo = findLogo(hint)
    return logoImg(logo ? logo.svg : GENERIC_LOGO, alt || (logo ? `${logo.name} Logo` : 'Logo'))
  })
}
